import { defineStore } from 'pinia'

import type { AccentLevel, Subdivision } from '~/utils/metronomeMeter'
import type { VoiceId } from '~/utils/metronomeVoices'
import {
  DEFAULT_BPM,
  DEFAULT_TIME_SIGNATURE,
  beatsPerBar,
  clampBpm,
  cycleAccent,
  defaultAccents,
  defaultSubdivision,
  formatTimeSignature,
  isCompoundCapable,
  parseTimeSignature,
  resizeAccents,
} from '~/utils/metronomeMeter'
import { DEFAULT_VOICE } from '~/utils/metronomeVoices'

/**
 * Metronome settings and window state.
 *
 * Settings live here rather than in the component so the floating dock can be
 * destroyed on close (freeing its AudioContext) and reopened with the user's
 * tempo, voice and EQ intact.
 */

const STORAGE_KEY = 'song7-metronome'

export interface MetronomeEqSettings {
  low: number
  mid: number
  high: number
}

export interface MetronomeSettings {
  bpm: number
  timeSignature: string
  compound: boolean
  subdivision: Subdivision
  voice: VoiceId
  volume: number
  pitch: number
  eq: MetronomeEqSettings
  muted: boolean
  syncEnabled: boolean
  /**
   * Accent patterns keyed by meter — a 6/8 pattern is meaningless in 4/4, and
   * the same signature counted in two vs six needs different patterns, so the
   * compound flag is part of the key.
   */
  accentPatterns: Record<string, AccentLevel[]>
}

const DEFAULTS: MetronomeSettings = {
  bpm: DEFAULT_BPM,
  timeSignature: DEFAULT_TIME_SIGNATURE,
  compound: false,
  subdivision: 1,
  voice: DEFAULT_VOICE,
  volume: 0.8,
  pitch: 0,
  eq: { low: 0, mid: 0, high: 0 },
  muted: false,
  // Off by default: sync costs up to a full bar of silence before the first
  // click, and most of the time you just want the metronome to start.
  syncEnabled: false,
  accentPatterns: {},
}

function load(): MetronomeSettings {
  if (!import.meta.client) return { ...DEFAULTS }

  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULTS }

    const parsed = JSON.parse(raw) as Partial<MetronomeSettings>
    return {
      ...DEFAULTS,
      ...parsed,
      eq: { ...DEFAULTS.eq, ...(parsed.eq ?? {}) },
      accentPatterns: { ...(parsed.accentPatterns ?? {}) },
    }
  } catch (error) {
    console.error('Error loading metronome settings:', error)
    return { ...DEFAULTS }
  }
}

export const useMetronomeStore = defineStore('metronome', () => {
  const settings = ref<MetronomeSettings>(load())

  /** Floating dock visibility (in-app only; the public page always renders). */
  const isOpen = ref(false)

  /**
   * Monotonic stop signal. Anything in the app can ask the metronome to stop
   * without holding a reference to the engine — the dock watches this. A
   * counter rather than the old boolean-pulse-with-a-timeout, so two stops in
   * quick succession can't collapse into one.
   */
  const stopRequests = ref(0)

  const beats = computed(() => beatsPerBar(settings.value.timeSignature, settings.value.compound))

  const accentKey = computed(() => `${settings.value.timeSignature}:${settings.value.compound ? 'c' : 's'}`)

  const accents = computed<AccentLevel[]>(() =>
    resizeAccents(settings.value.accentPatterns[accentKey.value], beats.value)
  )

  function persist() {
    if (!import.meta.client) return
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings.value))
    } catch (error) {
      console.error('Error saving metronome settings:', error)
    }
  }

  watch(settings, persist, { deep: true })

  function update<K extends keyof MetronomeSettings>(key: K, value: MetronomeSettings[K]) {
    settings.value[key] = value
  }

  function setBpm(value: number) {
    settings.value.bpm = clampBpm(value)
  }

  function nudgeBpm(delta: number) {
    setBpm(settings.value.bpm + delta)
  }

  function setTimeSignature(value: string) {
    const { numerator, denominator } = parseTimeSignature(value)
    const next = formatTimeSignature(numerator, denominator)

    settings.value.timeSignature = next
    // Compound only means anything for 6/8, 9/8, 12/8 — drop it otherwise so
    // the flag can't linger and silently regroup a later meter.
    if (!isCompoundCapable(next)) {
      settings.value.compound = false
    }
    settings.value.subdivision = defaultSubdivision(next, settings.value.compound)
  }

  function setCompound(value: boolean) {
    settings.value.compound = value && isCompoundCapable(settings.value.timeSignature)
    settings.value.subdivision = defaultSubdivision(settings.value.timeSignature, settings.value.compound)
  }

  function setAccent(index: number, level: AccentLevel) {
    const next = [...accents.value]
    if (index < 0 || index >= next.length) return
    next[index] = level
    settings.value.accentPatterns[accentKey.value] = next
  }

  function cycleAccentAt(index: number) {
    setAccent(index, cycleAccent(accents.value[index] ?? 1))
  }

  function resetAccents() {
    settings.value.accentPatterns[accentKey.value] = defaultAccents(beats.value)
  }

  /**
   * Seed tempo and meter from the song being viewed, leaving voice, EQ and
   * sync alone — those are the user's preferences, not the song's.
   */
  function applySongContext(bpm: string | number | null | undefined, timeSignature?: string | null) {
    const parsed = Number(bpm)
    if (Number.isFinite(parsed) && parsed > 0) {
      setBpm(parsed)
    }
    if (timeSignature) {
      setTimeSignature(timeSignature)
    }
  }

  function open() {
    isOpen.value = true
  }

  function close() {
    isOpen.value = false
  }

  /** Ask whatever is currently playing to stop (e.g. on a live-view song change). */
  function requestStop() {
    stopRequests.value++
  }

  return {
    settings,
    isOpen,
    stopRequests,
    beats,
    accents,
    update,
    setBpm,
    nudgeBpm,
    setTimeSignature,
    setCompound,
    setAccent,
    cycleAccentAt,
    resetAccents,
    applySongContext,
    open,
    close,
    requestStop,
  }
})
